// ./src/components/features/Categories/CategorSelector/CategorSelector.tsx
import React, { useMemo, useCallback, useEffect } from "react";
import { Select, SelectProps } from "@mantine/core";
import { useCategoriesContext } from "@/contexts/CategoriesContext";
import { LoadingIndicator } from "@/components/generic/LoadingIndicator";

export interface CategorSelectorProps
  extends Omit<SelectProps, "data" | "value" | "onChange"> {
  /** Selected category names */
  value?: string | null | undefined;

  /** Callback when category selection changes - receives array of category names */
  onChange?: (categoryId: string | null | undefined) => void;

  /** Placeholder text when no categories are selected */
  placeholder?: string;

  /** Custom filter function for categories */
  categoryFilter?: (category: any) => boolean;

  /** Whether the field is required */
  required?: boolean;

  /** Loading placeholder text */
  loadingPlaceholder?: string;

  /** Empty state text when no categories are available */
  emptyText?: string;
}

export const CategorSelector: React.FC<CategorSelectorProps> = ({
  value,
  onChange,
  placeholder = "Select category",
  categoryFilter,
  required = false,
  loadingPlaceholder = "Loading categories...",
  emptyText = "Start typing to create a category",
  error,
  disabled,
  size = "md",
  ...selectProps
}) => {
  const { categories, loading, isEmpty, lastError } = useCategoriesContext();

  // Filter and prepare category options (suggestions)
  const categoryOptions = useMemo(() => {
    let filteredCategories = categories;

    // Apply custom filter if provided
    if (typeof categoryFilter == "function") {
      filteredCategories = filteredCategories.filter(categoryFilter);
    }

    // Convert to array of category names for suggestions
    return filteredCategories
      .filter((category) => category.name) // Ensure category has a name
      .map((category) => {
        return {
          value: category.id || "",
          label: category.name || "N/A",
        };
      });
  }, [categories, categoryFilter]);

  // Handle onChange
  const handleChange = useCallback(
    (value: string | null) => {
      onChange?.(value);
    },
    [onChange]
  );

  // Auto-select first category type if required and no value is set
  useEffect(() => {
    if (required && !value && !loading && categories.length > 0 && onChange) {
      const firstOption = categories[0];
      if (firstOption) {
        onChange(firstOption.id);
      }
    }
  }, [required, loading, value, categories, onChange]);

  // Determine the current state and configure input props accordingly
  const getInputState = () => {
    // Loading state
    if (loading && categories.length === 0) {
      return {
        data: [],
        placeholder: loadingPlaceholder,
        disabled: true,
        rightSection: <LoadingIndicator size={16} color="green" />,
      };
    }

    // Error state
    if (lastError && !loading) {
      return {
        data: [],
        placeholder: "Error loading categories",
        disabled: true,
        error: error || "Failed to load categories",
      };
    }

    // Empty state (suggestions will be empty but user can still type)
    if (isEmpty && !loading) {
      return {
        data: [],
        placeholder: emptyText,
        disabled: disabled,
        rightSection: undefined,
      };
    }

    // Normal state with data
    return {
      data: categoryOptions,
      disabled: disabled || loading,
      rightSection:
        loading && categories.length > 0 ? (
          <LoadingIndicator size={16} color="green" />
        ) : (
          selectProps.rightSection
        ),
    };
  };

  const inputState = getInputState();

  // Render the tags input component
  return (
    <Select
      {...selectProps}
      size={size}
      value={value}
      onChange={handleChange}
      required={required}
      clearable={!required}
      error={inputState.error || error}
      data={inputState.data}
      placeholder={inputState.placeholder}
      disabled={inputState.disabled}
      rightSection={inputState.rightSection}
    />
  );
};
